/**
 * State 3 of 9 — permission denied (spec 01 §18, `PRODUCT.md` §4).
 *
 * **Driven by:** a `403` whose `error.code` is one of the four refusals the 02 §11
 * chain produces after step 2 —
 *
 *   `WORKFORCE_PERMISSION_DENIED`       step 3, the `workforce.*` code is missing
 *   `WORKFORCE_SCOPE_DENIED`            steps 4/5, the record is outside the scope
 *   `WORKFORCE_SENSITIVE_DATA_DENIED`   step 6, the sensitive-data gate
 *   `WORKFORCE_ACCESS_REASON_REQUIRED`  step 7, `X-Access-Reason` was not supplied
 *
 * — and, before any request is made, by the route guard comparing the item's
 * `requires` against `SessionUser.permissions`. Both paths land here on purpose: the
 * person sees the same refusal whether they clicked a link or typed the URL.
 *
 * **Never a redirect.** `PRODUCT.md` §3 and §4 are explicit, and the reasoning is
 * worth restating where the component lives: a refusal the user cannot see is a bug
 * report they cannot write. Sending an employee who typed `/productivity` back to
 * `/my-activity` leaves them believing the link was broken, and leaves support with
 * nothing to search for. So it renders in place, at the URL that was refused, with
 * the code and the correlation id on screen.
 *
 * The four sentences differ because the four refusals need different next actions:
 * a missing permission is a request to an administrator, a scope refusal is usually
 * the wrong employee id, and an access reason is something the person supplies
 * themselves.
 */

import type { ReactNode } from 'react';

import { useT } from '../i18n';
import type { MessageKey } from '../i18n/messages';
import type { PermissionDeniedCode } from '../api/errors';
import { StatusNote } from './StatusNote';
import { ErrorCodeLine } from './ErrorCodeLine';

const BODY_KEY: Readonly<Record<PermissionDeniedCode, MessageKey>> = {
  WORKFORCE_PERMISSION_DENIED: 'state.permissionDenied.WORKFORCE_PERMISSION_DENIED',
  WORKFORCE_SCOPE_DENIED: 'state.permissionDenied.WORKFORCE_SCOPE_DENIED',
  WORKFORCE_SENSITIVE_DATA_DENIED: 'state.permissionDenied.WORKFORCE_SENSITIVE_DATA_DENIED',
  WORKFORCE_ACCESS_REASON_REQUIRED: 'state.permissionDenied.WORKFORCE_ACCESS_REASON_REQUIRED',
};

export interface PermissionDeniedProps {
  /** Defaults to step 3, which is what the route guard refuses on. */
  readonly code?: PermissionDeniedCode;
  readonly correlationId?: string | null;
  readonly testId?: string;
}

export function PermissionDenied({
  code = 'WORKFORCE_PERMISSION_DENIED',
  correlationId = null,
  testId = 'state-permission-denied',
}: PermissionDeniedProps): ReactNode {
  const t = useT();
  return (
    <StatusNote
      severity="high"
      role="alert"
      title={t('state.permissionDenied.title')}
      testId={testId}
    >
      <p>{t(BODY_KEY[code])}</p>
      <p className="wp-muted">{t('common.contactAdmin')}</p>
      <ErrorCodeLine code={code} correlationId={correlationId} />
    </StatusNote>
  );
}
